import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  ButtonGroup,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import ConfirmationDialog from "../components/ConfirmationDialog";
import LazyLoader from "../components/LazyLoader";
import SearchField from "../components/SearchField";
import useSnackbar from "../components/snackbar/useSnackBar";
import { useApi } from "../hooks/useApi";

export default function Manufacturer() {
  const [manufacturers, setManufacturers] = useState([]);
  const [page, setPage] = useState(1);
  const [endReached, setEndReached] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateCounter, setUpdateCounter] = useState(1);
  const [query, setQuery] = useState("");

  const intl = useIntl();
  const api = useApi();

  const navigate = useNavigate();

  // snackbar for when deletion is not successful
  const { openSnackBar, closeSnackBar } = useSnackbar();

  const [isOpen, setIsOpen] = useState({ state: false, id: null });

  useEffect(() => {
    if (page === 1 || endReached) return;
    fetchPage(page, query);
  }, [page]);

  useEffect(() => {
    setPage(1);
    fetchPage(1, query);
  }, [query, updateCounter]);

  const fetchPage = (page, query) => {
    let fetchURL = `/manufacturer?page=${page - 1}&size=10`;
    if (query) {
      fetchURL += `&search=${query}`;
    }
    setIsLoading(true);
    api
      .get(fetchURL)
      .then(response => {
        if (page === 1) {
          setManufacturers(response.data.content);
        } else {
          setManufacturers(old => [...old, ...response.data.content]);
        }
        setEndReached(response.data.last);
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 403) {
          navigate('/login');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleOpen = (id) => {
    setIsOpen({ state: true, id: id });
  };
  const handleClose = () => {
    setIsOpen({ state: false, id: null });
  };

  const handleSearch = (value) => {
    setQuery(value);
  };

  function handleDelete(id) {
    api
      .delete(`/manufacturer/${id}`)
      .then((response) => {
        // handle the response
        handleClose();
        setManufacturers(oldManufacturers => oldManufacturers.filter(i => i.id !== id));
        setUpdateCounter(prev => prev + 1);
      })
      .catch((error) => {
        // handle errors
        console.log(error);
        handleClose();
        openSnackBar(intl.formatMessage({ id: "Overview.deletionFailure" }));
      });
  }



  const dialog = useMemo(() => {
    let d = (
      <ConfirmationDialog
        title={intl.formatMessage({ id: "Manufacturer.deletionQuestion" })}
        content={intl.formatMessage({ id: "Overview.deletionInfo" })}
        isOpen={isOpen.state}
        handleAccept={() => handleDelete(isOpen.id)}
        handleClose={handleClose}
      />
    );
    if (isOpen.state) {
      return d;
    }
  }, [isOpen]);

  const manufacturerList = useMemo(() => {
    return manufacturers.map((row) => (
      <TableRow
        key={row.id}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell component="th" scope="row">
          {row.name}
        </TableCell>
        <TableCell align="right">
          <ButtonGroup
            variant="contained"
            aria-label="outlined primary button group"
          >
            <Button
              component={Link}
              color="primary"
              aria-label="edit"
              to={"/manufacturer/" + row.id}
            >
              <EditIcon />
            </Button>
            <Button
              component={Link}
              onClick={() => handleOpen(row.id)}
              color="error"
              aria-label="delete"
            >
              <DeleteIcon />
            </Button>
          </ButtonGroup>
        </TableCell>
      </TableRow>
    ));
  }, [manufacturers]);

  return (
    <Container>
      <Button
        variant="contained"
        color="success"
        component={Link}
        to="/manufacturer/new"
        sx={{ float: "right" }}
      >
        <FormattedMessage id="Overview.create" />
      </Button>
      <h3><FormattedMessage id="Manufacturer.heading" /></h3>

      <SearchField onChange={(value) => handleSearch(value)} />

      <Box sx={{ width: "100%", height: "100%", margin: 0, padding: 0, marginTop: 1 }} >
        <LazyLoader
          onLoad={() => setPage(prev => prev + 1)}
          loading={isLoading}
          endReached={endReached}
        >
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell><FormattedMessage id="Manufacturer.name" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Overview.actions" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {manufacturerList}
              </TableBody>
            </Table>
          </TableContainer>
        </LazyLoader>
      </Box>
      {dialog}
    </Container>
  );
}
